import { getScopeFailureExplanationCopy } from '@frontend/api-client';
import { match } from '@frontend/duck-tape';
import { useEffect, useState } from '@frontend/react';
import type { DraftInformationGatheringComponentProps } from '@frontend/react-domain';
import { getIQTitle, useInitialQuestionsStep } from '@frontend/react-domain';
import type { ButtonProps } from '@frontend/web-react';
import { Button, InlineAlert, Spinner, Text, XStack, YStack } from '@frontend/web-react';
import { useSubmitDraftTaskIQAnswersMutation } from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';
import { CheckboxIQ } from './CheckboxIQ';
import { SecureNoteIQ } from './SecureNoteIQ';
import { TextInitialQuestion } from './TextInitialQuestion';

type InitialQuestionsProps = DraftInformationGatheringComponentProps & {
  goToDescriptionStep: () => void;
};

export const InitialQuestions = ({ changeStep, goToDescriptionStep }: InitialQuestionsProps) => {
  const {
    changeIQ,
    currentIQWithAnswer,
    isNextButtonDisabled,
    isNextButtonLoading,
    onChangeCheckboxIQ,
    onChangeSecureNoteIQ,
    onChangeTextIQ,
    state,
  } = useInitialQuestionsStep({
    changeStep,
    handleMutation,
    useSubmitDraftTaskIQAnswersMutation,
  });

  // Since secure note IQs are a little more complicated and need more control over the "next" button's state,
  // we need this is hack to support updating the parent UI from a child component
  const [NextButtonComponentOverride, setNextButtonComponentOverride] =
    useState<(props: ButtonProps) => React.ReactNode>();

  // If the current IQ is not a secure note, we don't need to show the override component
  useEffect(() => {
    if (currentIQWithAnswer?.type !== 'secure_note') {
      setNextButtonComponentOverride(undefined);
    }
  }, [currentIQWithAnswer?.type]);

  if (!currentIQWithAnswer) {
    return (
      <YStack alignItems="center" justifyContent="center" px="md" py="xl">
        <Spinner color="neutral.9" size="md" />
      </YStack>
    );
  }

  const currentQuestionIndex = state.initialQuestionsWithAnswers.findIndex(
    (q) => q.questionId === currentIQWithAnswer.questionId,
  );

  return (
    <YStack className="w-full">
      <YStack gapY="md" px="sm">
        {/* Makes question text align vertically with the text input */}
        <XStack gapX="sm" px={currentIQWithAnswer.type === 'text' ? 'md' : 'none'}>
          <XStack>
            <Text className="text-nowrap text-start" type="h4">
              {currentQuestionIndex + 2} →
            </Text>
          </XStack>
          <Text className="text-center" type="p-lg">
            {getIQTitle(currentIQWithAnswer)}
          </Text>
        </XStack>
        {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
        <YStack className="min-h-[100px]" gapY="lg">
          {match(currentIQWithAnswer)
            .with({ type: 'multi_choice' }, (iq) => (
              <CheckboxIQ
                initialQuestionWithAnswer={iq}
                isLoading={isNextButtonLoading}
                onChange={onChangeCheckboxIQ(iq)}
              />
            ))
            .with({ type: 'text' }, (iq) => (
              <TextInitialQuestion
                initialQuestionWithAnswer={iq}
                isLoading={isNextButtonLoading}
                onChange={onChangeTextIQ(iq)}
              />
            ))
            .with({ type: 'secure_note' }, (iq) => (
              <SecureNoteIQ
                initialQuestionWithAnswer={iq}
                isLoading={isNextButtonLoading}
                onChange={(iq) => onChangeSecureNoteIQ(iq)}
                onSuccess={async (iq) => changeIQ('increment', iq)}
                setNextButtonComponentOverride={setNextButtonComponentOverride}
              />
            ))
            .exhaustive()}
          {state.scopeFailureReason ? (
            <YStack>
              <InlineAlert description={getScopeFailureExplanationCopy(state.scopeFailureReason)} variant="error" />
            </YStack>
          ) : null}
        </YStack>

        <XStack justifyContent="between">
          {state.scopeFailureReason ? (
            <XStack alignItems="center">
              <Button label="Update Description" onClick={goToDescriptionStep} variant="primary" />
            </XStack>
          ) : (
            <XStack gapX="md">
              <Button
                disabled={isNextButtonLoading}
                label="Back"
                onClick={() => changeIQ('decrement')}
                variant="secondary"
              />
              {NextButtonComponentOverride ? (
                <NextButtonComponentOverride
                  disabled={isNextButtonDisabled}
                  isLoading={isNextButtonLoading}
                  label="Next"
                />
              ) : (
                <Button
                  disabled={isNextButtonDisabled}
                  isLoading={isNextButtonLoading}
                  label="Next"
                  onClick={() => changeIQ('increment')}
                  variant="primary"
                />
              )}
            </XStack>
          )}
        </XStack>
      </YStack>
    </YStack>
  );
};
